import { useRef, useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dae18c3ep053ijub16q0";

export default function Newsletter() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = ((data.get("website_alt") as string) || "").trim();

    // 反垃圾：命中蜜罐时给出与正常一致的成功反馈，但不发送任何请求
    if (honeypot) {
      setMessage("订阅成功，欢迎加入看吃瓜周报。");
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    for (const [key, value] of data.entries()) {
      if (key === "website_alt") continue;
      payload.append(key, String(value));
    }

    setSubmitting(true);
    setStatus("idle");
    setMessage("");
    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || (parsed as { message?: string })?.message || "";
      const looksSpam = /spam/i.test(serverMsg + responseText);
      const ok = response.ok && parsed?.code === "OK";

      if (ok && !looksSpam) {
        setMessage("订阅成功，欢迎加入看吃瓜周报，每周五为你汇总本周值得细看的深扒内容。");
        setStatus("success");
        form.reset();
      } else {
        setMessage(serverMsg || "订阅没有成功，请稍后再试一次。");
        setStatus("error");
      }
    } catch {
      setMessage("网络似乎开小差了，请稍后再试一次。");
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="subscribe" className="scroll-anchor bg-primary-500 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-3 py-1 text-xs font-bold text-background-50">
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              看吃瓜周报
            </span>
            <h2 className="mt-5 font-heading text-2xl font-black leading-tight text-background-50 md:text-4xl">
              每周五，把值得细看的瓜汇总给你
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-background-50/85 md:text-base">
              一键订阅编辑部周报：本周最值得深扒的事件、被忽略的线索、热榜的完整脉络，都在一封邮件里整理清楚。
              免费、不发垃圾、随时可退订。
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-background-50/90">
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line" aria-hidden="true"></i>
                每周 1 封，准时送达
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line" aria-hidden="true"></i>
                无广告骚扰
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line" aria-hidden="true"></i>
                一键退订
              </span>
            </div>
          </div>

          <div className="lg:col-span-6">
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-readdy-form="看吃瓜周报订阅"
              className="rounded-xl bg-background-50 p-6 md:p-8"
            >
              <p className="text-lg font-bold text-foreground-950">订阅看吃瓜周报</p>
              <p className="mt-1 text-sm text-foreground-600">填写邮箱，每周不错过重点深扒。</p>

              <div className="mt-5 flex flex-col gap-3">
                <label htmlFor="nl-email" className="sr-only">
                  电子邮箱
                </label>
                <input
                  id="nl-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="你的邮箱地址"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/60"
                />
                {/* 反垃圾蜜罐字段，对真人不可见 */}
                <label htmlFor="nl-website" className="hp-field">
                  网站地址
                  <input
                    id="nl-website"
                    type="text"
                    name="website_alt"
                    autoComplete="off"
                    tabIndex={-1}
                    readOnly
                    aria-hidden="true"
                  />
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill" aria-hidden="true"></i>
                      立即订阅
                    </>
                  )}
                </button>
              </div>

              {status === "success" && (
                <p className="mt-4 flex items-start gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-800" role="status">
                  <i className="ri-checkbox-circle-fill mt-0.5" aria-hidden="true"></i>
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800" role="alert">
                  <i className="ri-alert-fill mt-0.5" aria-hidden="true"></i>
                  {message}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}